import {Component, OnInit} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {PasswordHttpService} from "../_services/password-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {AppRoutes} from "../../../enums/routes";
import {PasswordResetKind, passwordResetKindFrom} from "../password-reset-kind";

/**
 * "Passwort vergessen" — for both credential stores (#279).
 *
 * Which store is asked is decided by the **link that was pressed**, never by anything typed here: `/login` shows
 * two forms and each carries its own link, so the person has already said which account they mean before they
 * arrive. The kind travels as `?kind=account`; without it this is the employee flow it has always been.
 *
 * **The screen never reports whether the address exists**, and the wording is chosen so that it cannot. Both
 * endpoints answer 200 in every case, so a screen saying "we have sent you a mail" would be asserting something
 * it was never told — and would be wrong for exactly the address an attacker is probing. The confirmation is
 * therefore conditional.
 */
@Component({
  selector: 'app-forgot-password',
  imports: [
    FormsModule
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent implements OnInit {
  email: string = "";

  /** Which credential store this run is for; `legacy` unless the link said otherwise. */
  protected kind: PasswordResetKind = 'legacy';

  constructor(private passwordHttpService: PasswordHttpService, private gas: GlobalAlertService,
              private router: Router, private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.kind = passwordResetKindFrom(params['kind']);
    });
  }

  /**
   * Whether this is the native-account flow, which the template uses for its heading and its explanatory line.
   *
   * The two need different words rather than one neutral phrasing: an employee reset reaches a company account
   * their administrator manages, a native reset reaches an account that belongs to nobody but them. Somebody who
   * lands on the wrong one should be able to tell immediately, because the mail they are waiting for will
   * otherwise never arrive and nothing will have told them why.
   */
  protected get isAccount(): boolean {
    return this.kind === 'account';
  }

  reset() {
    if (this.email.isNullOrWhitespace()) {
      this.gas.createAndShow("Fehler", "Bitte geben Sie eine E-Mail-Adresse ein.", AlertLevel.error);
      return;
    }

    this.passwordHttpService.forgotPassword(this.email, this.kind)
      .withLoadingIndicator()
      .subscribe({
        next: () => {
          this.router.navigate(['/' + AppRoutes.Login]).then(_ => {
            // Deliberately conditional ("Falls …"). See the class remarks: the server answers 200 whether or not
            // the address has an account, so anything more definite would be a claim this screen cannot support
            // — and the difference between the two sentences would be the oracle.
            this.gas.createAndShow("Erfolg",
              "Falls es zu dieser Adresse ein Konto gibt, ist eine E-Mail unterwegs. Der Link gilt zwei " +
              "Stunden und kann einmal verwendet werden.", AlertLevel.success);
            this.email = "";
          });
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Ein Fehler ist aufgetreten. Bitte Eingaben überprüfen.", AlertLevel.error);
        }
      })
  }
}
